import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";

import {
  FaArrowRight,
  FaCheck,
  FaInfoCircle,
  FaRegClock,
  FaRegFileAlt,
  FaRobot,
  FaTimes
} from "react-icons/fa";

import ApprovalService from "../../services/api/approval";
import "../../assets/css/auth.css";


const APPROVAL_ACTION = {
  APPROVE: "approve",
  REJECT: "reject"
};

const RESULT_TYPE = {
  APPROVED: "approved",
  REJECTED: "rejected",
  PROCESSED: "processed",
  ERROR: "error"
};


const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};


const ApprovalRedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const processedSearchRef = useRef("");

  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [response, setResponse] = useState(null);
  const [resultType, setResultType] = useState("");

  useEffect(() => {
    if (processedSearchRef.current === location.search) {
      return;
    }

    processedSearchRef.current = location.search;

    const handleApproval = async () => {
      const params = new URLSearchParams(location.search);

      const requestId = params.get("requestId") || "";
      const action = params.get("action") || "";
      const token = params.get("token") || "";

      if (!requestId || !action || !token) {
        setResultType(RESULT_TYPE.ERROR);
        setMessage("Invalid Approval Request");
        setResponse({
          error: "Missing requestId, action, or token"
        });
        setIsLoading(false);
        return;
      }

      if (
        action !== APPROVAL_ACTION.APPROVE &&
        action !== APPROVAL_ACTION.REJECT
      ) {
        setResultType(RESULT_TYPE.ERROR);
        setMessage("Invalid Approval Action");
        setResponse({
          error: `Unsupported action: ${action}`
        });
        setIsLoading(false);
        return;
      }

      try {
        const result =
          action === APPROVAL_ACTION.APPROVE
            ? await ApprovalService.approve(requestId, token)
            : await ApprovalService.reject(requestId, token);

        if (result?.status === "FAILED") {
          const backendMessage =
            result?.data?.message ||
            result?.errorMessage ||
            "Unable to process approval request";

          const alreadyProcessed = backendMessage
            .toLowerCase()
            .includes("already");

          if (alreadyProcessed) {
            setResultType(RESULT_TYPE.PROCESSED);
            setMessage("Approval Request Already Processed");
          } else {
            setResultType(RESULT_TYPE.ERROR);
            setMessage("Unable to Process Approval Request");
          }

          setResponse({
            error: backendMessage
          });

          return;
        }

        const controllerResponse = result?.data ?? result;

        setResponse(controllerResponse);

        if (action === APPROVAL_ACTION.APPROVE) {
          setResultType(RESULT_TYPE.APPROVED);
          setMessage("Approval Successful");
        } else {
          setResultType(RESULT_TYPE.REJECTED);
          setMessage("Request Rejected");
        }

      } catch (error) {
        const status = error?.response?.status;
        const controllerResponse = error?.response?.data;

        if (status === 409) {
          setResultType(RESULT_TYPE.PROCESSED);
          setMessage("Approval Request Already Processed");
          setResponse({
            error:
              controllerResponse?.message ||
              "This approval request has already been processed"
          });
          return;
        }

        setResultType(RESULT_TYPE.ERROR);

        setResponse({
          error:
            controllerResponse?.message ||
            controllerResponse?.error ||
            error?.message ||
            "Unable to process approval request"
        });

        if (status === 400) {
          setMessage("Invalid Approval Request");
        } else if (status === 401) {
          setMessage("Unauthorized Approval Request");
        } else if (status === 403) {
          setMessage("Approval Request Forbidden");
        } else if (status === 404) {
          setMessage("Approval Request Not Found");
        } else {
          setMessage("Unable to Process Approval Request");
        }

      } finally {
        setIsLoading(false);
      }
    };

    handleApproval();

  }, [location.search]);


  const isApproved =
    resultType === RESULT_TYPE.APPROVED;

  const isRejected =
    resultType === RESULT_TYPE.REJECTED;

  const isProcessed =
    resultType === RESULT_TYPE.PROCESSED;

  const isError =
    resultType === RESULT_TYPE.ERROR;


  const approvalStatus =
    response?.approvalStatus ||
    response?.status ||
    (
      isApproved
        ? "APPROVED"
        : isRejected
          ? "REJECTED"
          : null
    );


  const handleBackToDashboard = () => {
    navigate("/");
  };


  return (
    <div className="approval-page">

      {isLoading ? (

        <div className="approval-loading">
          <ClipLoader size={48} />
          <p>Processing approval...</p>
        </div>

      ) : (

        <div
          className={`approval-card ${
            isApproved
              ? "approval-card--success"
              : isRejected
                ? "approval-card--rejected"
                : "approval-card--error"
          }`}
        >

          <div
            className={`approval-status-icon ${
              isApproved
                ? "approval-status-icon--success"
                : "approval-status-icon--rejected"
            }`}
          >
            {isApproved ? (
              <FaCheck />
            ) : isProcessed ? (
              <FaInfoCircle />
            ) : (
              <FaTimes />
            )}
          </div>

          <h2 className="approval-title">
            {message}
          </h2>

          <p className="approval-subtitle">
            {isApproved
              ? "The workflow approval request has been approved successfully."
              : isRejected
                ? "The workflow approval request has been rejected."
                : isProcessed
                  ? "This approval request has already been processed."
                  : "We could not complete this approval request."}
          </p>

          {!isError && !isProcessed && response && (

            <div className="approval-details">

              {response?.requestId && (
                <div className="approval-detail-row">

                  <div className="approval-detail-label">
                    <span className="approval-detail-icon">
                      <FaRegFileAlt />
                    </span>

                    <span>Request ID</span>
                  </div>

                  <div className="approval-detail-value">
                    {response.requestId}
                  </div>

                </div>
              )}

              {response?.agentName && (
                <div className="approval-detail-row">

                  <div className="approval-detail-label">
                    <span className="approval-detail-icon">
                      <FaRobot />
                    </span>

                    <span>Agent</span>
                  </div>

                  <div className="approval-detail-value">
                    {response.agentName}
                  </div>

                </div>
              )}

              {approvalStatus && (
                <div className="approval-detail-row">

                  <div className="approval-detail-label">
                    <span className="approval-detail-icon">
                      {isApproved ? (
                        <FaCheck />
                      ) : (
                        <FaTimes />
                      )}
                    </span>

                    <span>Status</span>
                  </div>

                  <div className="approval-detail-value">
                    <span
                      className={`approval-status-badge ${
                        isApproved
                          ? "approval-status-badge--success"
                          : "approval-status-badge--rejected"
                      }`}
                    >
                      {approvalStatus}
                    </span>
                  </div>

                </div>
              )}

              {response?.respondedAt && (
                <div className="approval-detail-row">

                  <div className="approval-detail-label">
                    <span className="approval-detail-icon">
                      <FaRegClock />
                    </span>

                    <span>
                      {isApproved
                        ? "Approved At"
                        : "Rejected At"}
                    </span>
                  </div>

                  <div className="approval-detail-value">
                    {formatDateTime(response.respondedAt)}
                  </div>

                </div>
              )}

            </div>
          )}

          <div
            className={`approval-info ${
              isApproved
                ? "approval-info--success"
                : "approval-info--rejected"
            }`}
          >
            <FaInfoCircle />

            <span>
              {isApproved
                ? "The workflow will continue automatically."
                : isRejected
                  ? "This workflow request will not continue."
                  : isProcessed
                    ? response?.error ||
                      "This approval request has already been processed."
                    : response?.error ||
                      "Please verify the approval link and try again."}
            </span>
          </div>

          <button
            type="button"
            className={`approval-action-btn ${
              isApproved
                ? "approval-action-btn--success"
                : "approval-action-btn--rejected"
            }`}
            onClick={handleBackToDashboard}
          >
            <FaArrowRight />
            Back to Dashboard
          </button>

        </div>
      )}

    </div>
  );
};

export default ApprovalRedirectHandler;